// Batched, non-blocking diagnostics writer.
//
// GUARANTEE: nothing in this module may throw into a caller and nothing here
// is awaited on a wallet/bet critical path. report() is fire-and-forget: it
// pushes into an in-memory queue and returns the error id synchronously.

import { supabase } from "@/integrations/supabase/client";
import { getInstalledVersionSync, getInstalledBuildSync } from "@/lib/version";
import { getRunningBundleId } from "@/lib/bundle-version";
import { redactObject, redactString } from "./redact";
import { currentCorrelationId } from "./correlation";
import { groupHash, newErrorId } from "./ids";
import { noteDropped, noteFlush, noteQueued } from "./health";
import {
  isRetryable,
  rateDuration,
  safeMessageFor,
  type DiagCategory,
  type DiagnosticEvent,
  type DiagSeverity,
  type PerfMetricName,
  type WalletImpact,
} from "./types";

const FLUSH_MS = 4000;
const MAX_BATCH = 25;
const MAX_QUEUE = 200;
const DEDUPE_WINDOW_MS = 15_000;

let eventQueue: Record<string, unknown>[] = [];
let perfQueue: Record<string, unknown>[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let cachedUserId: string | null = null;
const seen = new Map<string, number>();

function platform(): string {
  if (typeof window === "undefined") return "server";
  const cap = (window as any).Capacitor;
  if (cap?.getPlatform) {
    try {
      return String(cap.getPlatform());
    } catch {
      /* ignore */
    }
  }
  return "web";
}

function currentPage(): string | undefined {
  return typeof window !== "undefined" ? window.location.pathname : undefined;
}

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = seen.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  seen.set(key, now);
  if (seen.size > 200) for (const [k, t] of seen) if (now - t > 60_000) seen.delete(k);
  return false;
}

async function resolveUserId(): Promise<string | null> {
  if (cachedUserId) return cachedUserId;
  try {
    const { data } = await supabase.auth.getSession();
    cachedUserId = data.session?.user?.id ?? null;
  } catch {
    cachedUserId = null;
  }
  return cachedUserId;
}

function schedule() {
  if (timer || typeof window === "undefined") return;
  timer = setTimeout(() => {
    timer = null;
    void flushDiagnostics();
  }, FLUSH_MS);
}

/** Sends whatever is queued. Never rejects. */
export async function flushDiagnostics(): Promise<void> {
  const events = eventQueue.splice(0, MAX_BATCH);
  const perf = perfQueue.splice(0, MAX_BATCH);
  if (!events.length && !perf.length) return;
  try {
    const uid = await resolveUserId();
    if (events.length) {
      await supabase
        .from("diagnostics_events")
        .insert(events.map((e) => ({ ...e, user_id: e.user_id ?? uid })) as any);
    }
    if (perf.length) {
      await supabase
        .from("perf_metrics")
        .insert(perf.map((p) => ({ ...p, user_id: p.user_id ?? uid })) as any);
    }
    noteFlush(true, eventQueue.length + perfQueue.length);
  } catch {
    // Diagnostics must never surface or retry-storm. Drop the batch.
    noteFlush(false, eventQueue.length + perfQueue.length);
    noteDropped(events.length + perf.length);
  }
  if (eventQueue.length || perfQueue.length) schedule();
}

export type ReportInput = {
  category: DiagCategory;
  code: string;
  stage?: string;
  technical_message?: string;
  safe_message?: string;
  page?: string;
  route?: string;
  request_id?: string;
  correlation_id?: string;
  duration_ms?: number;
  severity?: DiagSeverity;
  retryable?: boolean;
  wallet_impact?: WalletImpact;
  metadata?: Record<string, unknown>;
  error?: unknown;
};


/**
 * Records a diagnostic and returns the user-showable error id immediately.
 * Synchronous by design — safe to call inside catch blocks on any path.
 */
export function report(input: ReportInput): { error_id: string; safe_message: string } {
  const error_id = newErrorId(input.category);
  const safe_message = input.safe_message ?? safeMessageFor(input.category, input.code);
  try {
    const page = input.page ?? currentPage();
    const technical =
      input.technical_message ??
      (input.error ? String((input.error as any)?.message ?? input.error) : undefined);

    const gh = groupHash([input.category, input.code, input.stage, page, technical?.slice(0, 120)]);
    if (isDuplicate(gh)) return { error_id, safe_message };

    const stack = (input.error as any)?.stack;

    const row: Record<string, unknown> = {
      error_id,
      category: input.category,
      code: input.code,
      safe_message,
      technical_message: technical ? redactString(technical).slice(0, 2000) : null,
      page: page ?? null,
      route: input.route ?? page ?? null,
      stage: input.stage ?? null,
      request_id: input.request_id ?? null,
      correlation_id: input.correlation_id ?? currentCorrelationId(),

      app_version: getInstalledVersionSync() ?? null,
      build_id: getRunningBundleId() ?? null,
      platform: platform(),
      duration_ms: typeof input.duration_ms === "number" ? Math.round(input.duration_ms) : null,
      retryable: input.retryable ?? isRetryable(input.code),
      severity: input.severity ?? "error",
      wallet_impact: input.wallet_impact ?? "not_applicable",
      group_hash: gh,
      metadata: redactObject({
        ...(input.metadata ?? {}),
        app_build: getInstalledBuildSync() ?? null,
        stack: stack ? String(stack).slice(0, 6000) : null,
        online: typeof navigator !== "undefined" ? navigator.onLine : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        viewport:
          typeof window !== "undefined"
            ? `${window.innerWidth}x${window.innerHeight}`
            : null,
      }),
      created_at: new Date().toISOString(),
    };

    if (eventQueue.length < MAX_QUEUE) {
      eventQueue.push(row);
      noteQueued("event", eventQueue.length);
    } else noteDropped();
    if (input.severity === "critical") void flushDiagnostics();
    else schedule();
  } catch {
    // never break the caller
  }
  return { error_id, safe_message };
}

/** Builds the in-memory contract object (used by tests and the UI). */
export function toDiagnosticEvent(input: ReportInput, error_id: string): DiagnosticEvent {
  return {
    error_id,
    category: input.category,
    code: input.code,
    safe_message: input.safe_message ?? safeMessageFor(input.category, input.code),
    technical_message: input.technical_message,
    page: input.page ?? currentPage(),
    stage: input.stage,
    request_id: input.request_id,
    duration_ms: input.duration_ms,
    retryable: input.retryable ?? isRetryable(input.code),
    severity: input.severity ?? "error",
    wallet_impact: input.wallet_impact ?? "not_applicable",
    metadata: redactObject(input.metadata) as DiagnosticEvent["metadata"],
    created_at: new Date().toISOString(),
  };
}

export function recordPerf(
  metric: PerfMetricName,
  duration_ms: number,
  extra?: { page?: string; request_id?: string; metadata?: Record<string, unknown> },
): void {
  try {
    if (!Number.isFinite(duration_ms) || duration_ms < 0) return;
    const rating = rateDuration(metric, duration_ms);
    if (perfQueue.length < MAX_QUEUE) {
      perfQueue.push({
        metric,
        duration_ms: Math.round(duration_ms),
        rating,
        page: extra?.page ?? currentPage() ?? null,
        app_version: getInstalledVersionSync() ?? null,
        build_id: getRunningBundleId() ?? null,
        platform: platform(),
        request_id: extra?.request_id ?? null,
        metadata: redactObject(extra?.metadata),
        created_at: new Date().toISOString(),
      });
      noteQueued("event", perfQueue.length);
    }
    schedule();
  } catch {
    /* never break the caller */
  }
}

export function resetDiagnosticsUserCache() {
  cachedUserId = null;
}

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushDiagnostics();
  });
  window.addEventListener("pagehide", () => void flushDiagnostics());
}
